import React from "react";
import BespokeWizard from "@/components/bespoke/BespokeWizard";
import { BESPOKE_CATEGORIES } from "@/lib/bespokeCategories";
import { UpperBodyIcon, ShoulderTypeIcon } from "@/components/bespoke/ShirtIcons";
import { LapelIcon, VentIcon, ButtonStanceIcon, JacketPocketIcon, PleatIcon, HemIcon } from "@/components/bespoke/GarmentIcons";

const UPPER_BODY_OPTIONS = [
  { key: "athletic", label: "Athletic", help: "Broader shoulders, narrower waist." },
  { key: "average", label: "Average", help: "Balanced, proportionate build." },
  { key: "healthy", label: "Healthy", help: "Fuller through the chest and midsection." },
];
const SHOULDER_OPTIONS = [{ key: "average", label: "Average", help: "Level shoulder line." }, { key: "sloping", label: "Sloping", help: "Shoulders angle downward from the neck." }];
const CONSTRUCTION_OPTIONS = [{ key: "half", label: "Half Canvas", price: 0 }, { key: "full", label: "Full Canvas", price: 3500 }];
const LAPEL_OPTIONS = [{ key: "notch", label: "Notch" }, { key: "peak", label: "Peak" }, { key: "shawl", label: "Shawl" }];
const VENT_OPTIONS = [{ key: "single", label: "Single Vent" }, { key: "double", label: "Double Vent" }, { key: "none", label: "No Vent" }];
const BUTTON_OPTIONS = [{ key: "1", label: "1-Button" }, { key: "2", label: "2-Button" }, { key: "3", label: "3-Button" }];
const JACKET_POCKET_OPTIONS = [{ key: "flap", label: "Flap" }, { key: "jetted", label: "Jetted" }, { key: "patch", label: "Patch" }];
const LINING_OPTIONS = [{ key: "standard", label: "Standard", price: 0 }, { key: "silk", label: "Silk Patterned", price: 800 }, { key: "contrast", label: "Contrast", price: 500 }];
const PLEAT_OPTIONS = [{ key: "flat", label: "Flat Front" }, { key: "single", label: "Single Pleat" }, { key: "double", label: "Double Pleat" }];
const HEM_OPTIONS = [{ key: "plain", label: "Plain Hem" }, { key: "cuffed", label: "Cuffed Hem" }];

const config = {
  garmentType: "suit",
  title: "Bespoke Suit",
  prefix: "suit",
  guideCaption: "For optimal fit, we recommend measuring from your best-fitting suit.",
  faqs: BESPOKE_CATEGORIES.suit.faqs,
  groups: [
    { group: "upper_body_shape", label: "Upper Body Shape *", options: UPPER_BODY_OPTIONS, Icon: UpperBodyIcon, default: "average" },
    { group: "shoulder_type", label: "Shoulder Type *", options: SHOULDER_OPTIONS, Icon: ShoulderTypeIcon, default: "average", cols: "grid-cols-2", maxWidth: "max-w-sm" },
    { group: "construction", label: "Construction", options: CONSTRUCTION_OPTIONS, default: "half", cols: "grid-cols-2", maxWidth: "max-w-sm", showPrice: true, addon: true },
    { group: "lapel", label: "Lapel", options: LAPEL_OPTIONS, Icon: LapelIcon, default: "notch" },
    { group: "vent", label: "Vent", options: VENT_OPTIONS, Icon: VentIcon, default: "single" },
    { group: "button_stance", label: "Button Stance", options: BUTTON_OPTIONS, Icon: ButtonStanceIcon, default: "2" },
    { group: "jacket_pocket", label: "Jacket Pocket", options: JACKET_POCKET_OPTIONS, Icon: JacketPocketIcon, default: "flap" },
    { group: "lining", label: "Lining", options: LINING_OPTIONS, default: "standard", showPrice: true, addon: true },
    { group: "pleat", label: "Trouser Pleat", options: PLEAT_OPTIONS, Icon: PleatIcon, default: "flat" },
    { group: "hem", label: "Trouser Hem", options: HEM_OPTIONS, Icon: HemIcon, default: "plain", cols: "grid-cols-2", maxWidth: "max-w-sm" },
  ],
  sizes: ["S", "M", "L", "XL", "XXL", "3XL", "4XL", "5XL"],
  sizeChart: {
    columns: ["Chest (in)", "Front Length (in)", "Sleeves (in)", "Across Shoulder (in)"],
    rows: [
      { size: "S", values: [38, 28, 24, 17] },
      { size: "M", values: [40, 28.5, 24.5, 17.5] },
      { size: "L", values: [42, 29, 25, 18] },
      { size: "XL", values: [44, 29.5, 25.5, 18.5] },
      { size: "XXL", values: [46, 30, 25.5, 19] },
      { size: "3XL", values: [48, 30.5, 26, 19.5] },
      { size: "4XL", values: [50, 31, 26, 20] },
      { size: "5XL", values: [52, 31, 26, 20.5] },
    ],
  },
  measureFields: [
    { key: "body_height", label: "Body Height *", type: "text", target: "style", required: true },
    { key: "chest", label: "Chest (in) *", target: "meas", required: true },
    { key: "waist", label: "Waist (in) *", target: "meas", required: true },
    { key: "shoulder", label: "Shoulder (in) *", target: "meas", required: true },
    { key: "sleeve", label: "Sleeve Length (in)", target: "meas" },
    { key: "jacket_length", label: "Jacket Length (in) *", target: "meas", required: true },
    { key: "seat", label: "Seat (in)", target: "meas" },
    { key: "trouser_inseam", label: "Inseam (in)", target: "meas" },
  ],
};

export default function BespokeSuit() {
  return <BespokeWizard config={config} />;
}
